import { useRef } from "react";
import { Link } from "react-router-dom";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import { t, formatEur } from "@/i18n/sk";
import { useCart } from "@/context/CartContext";
import { StockBadge } from "@/components/StockBadge";
import { resolveImg } from "@/lib/api";

export function CardCard({ card, index = 0 }) {
  const ref = useRef(null);
  const { addItem, setOpen } = useCart();
  const img = resolveImg(card.images?.[0]);
  const buyable = card.price_eur != null && card.availability !== "sold_out";

  const handleMove = (e) => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    el.style.setProperty("--mx", `${((e.clientX - rect.left) / rect.width) * 100}%`);
    el.style.setProperty("--my", `${((e.clientY - rect.top) / rect.height) * 100}%`);
  };

  const handleAdd = (e) => {
    e.preventDefault();
    if (!buyable) return;
    addItem({ id: card.id, slug: card.slug, name: card.name, price_eur: card.price_eur, images: card.images, expansion_name: card.expansion_name }, 1);
    toast.success(t.actions.added, { description: card.name });
    setOpen(true);
  };

  return (
    <Link
      to={`/karta/${card.slug}`}
      ref={ref}
      onMouseMove={handleMove}
      data-testid={`card-card-${card.slug}`}
      className="group relative flex flex-col overflow-hidden rounded-2xl border border-white/8 bg-vault-card p-4 transition-all duration-300 hover:border-cyan-glow/50 hover:shadow-[0_16px_50px_rgba(6,182,212,0.2)] animate-fade-up"
      style={{ animationDelay: `${index * 70}ms` }}
    >
      <span className="holo-sweep" />
      <div className="holo-ring relative mb-4 flex aspect-[3/4] items-center justify-center overflow-hidden rounded-xl border border-white/8 bg-vault-black/60 p-3">
        {card.rarity && (
          <span className="absolute left-3 top-3 z-10 rounded-full border border-cyan-glow/40 bg-vault-black/70 px-2.5 py-1 text-[0.6rem] font-semibold uppercase tracking-wider text-cyan-200 backdrop-blur">
            {card.rarity}
          </span>
        )}
        {card.placeholder_image && (
          <span className="absolute bottom-3 right-3 z-10 rounded-full border border-white/15 bg-vault-black/70 px-2 py-0.5 text-[0.6rem] font-medium text-slate-300 backdrop-blur">{t.product.illustrative}</span>
        )}
        {img ? <img src={img} alt={card.name} loading="lazy" className="h-full w-full object-contain transition-transform duration-500 group-hover:scale-105" /> : <div className="text-sm text-slate-600">Bez obrázka</div>}
      </div>
      <div className="flex flex-1 flex-col">
        <span className="overline mb-1 text-[0.65rem]">{card.expansion_name}{card.card_number ? ` · ${card.card_number}` : ""}</span>
        <h3 className="mb-3 line-clamp-2 text-base font-bold leading-snug text-white">{card.name}</h3>
        <div className="mt-auto flex items-end justify-between gap-2 pt-2">
          <div>
            <StockBadge availability={card.availability} testId={`card-stock-${card.slug}`} />
            <div className="mt-2 font-mono text-lg font-extrabold text-white">
              {card.price_eur != null ? formatEur(card.price_eur) : <span className="text-sm font-medium text-slate-400">{t.product.priceOnRequest}</span>}
            </div>
          </div>
          <button onClick={handleAdd} disabled={!buyable} data-testid={`card-add-to-cart-${card.slug}`} aria-label={t.actions.addToCart} className="btn-glow flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-violet-glow to-cyan-glow text-white disabled:cursor-not-allowed disabled:opacity-40 disabled:shadow-none">
            <Plus className="h-5 w-5" />
          </button>
        </div>
      </div>
    </Link>
  );
}

export default CardCard;
